import jsPDF from "jspdf";
import logo from "./assets/logo.png";
import autoTable from "jspdf-autotable";
import { AMIRI_BASE64 } from "./assets/fonts/Amiri-Regular.base64";

const fmt = (v) => (v == null ? "" : String(v));
const pad = (n) => {
  const x = String(n || "");
  return x.length === 1 ? `0${x}` : x;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fmt(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatMoney = (value) => {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export default function generateBolInvoicePdf({
  bol,
  header,
  days,
  d_no,
  invoice_no,
  savedInvoiceAmount,
  savedReceiptAmount,
  masterServiceFee,
  servicesList = [],
  packingItems = [],
  buildUpItems = [],
  currency,
  exchangeRate = 2500
}) {
  const chosenCurrency = String(currency ?? "").trim().toUpperCase();
  const storedCurrency = String(bol?.amount_type ?? "").trim().toUpperCase();
  const effectiveCurrency = chosenCurrency || storedCurrency || "USD";
  const isSdg = effectiveCurrency === "SDG";
  const rateMultiplier = isSdg ? Number(exchangeRate || 2500) : 1;
  const currencySymbol = isSdg ? "SDG " : "$";
  const currencyLabel = isSdg ? "SDG" : "USD";

  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  doc.addFileToVFS("Amiri-Regular.ttf", AMIRI_BASE64);
  doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
  doc.setFont("Amiri", "normal");
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Logo
  const logoWidth = 180;
  const logoHeight = 45;
  const logoX = (pageW - logoWidth) / 2;
  const logoY = 20;
  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

  // Title
  doc.setFontSize(16);
  doc.setFont("Amiri", "bold");
  doc.text("INVOICE", margin, logoY + logoHeight + 30);
  doc.setFontSize(10);
  doc.setFont("Amiri", "normal");
  doc.text("I.C.A.O. Annex 9, Appendix 3", pageW - margin, logoY + logoHeight + 30, { align: "right" });

  doc.setLineWidth(1.5);
  doc.line(margin, logoY + logoHeight + 35, pageW - margin, logoY + logoHeight + 35);

  const getVal = (obj, key) => {
    if (!obj) return undefined;
    const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    return obj[key] !== undefined ? obj[key] : obj[camelKey];
  };

  const normalizeType = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

  const parseIdArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(Number).filter(Boolean);

    let str = String(val).trim();
    if (!str) return [];

    try {
      let parsed = JSON.parse(str);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(Boolean);
      if (parsed && typeof parsed === 'object') return Object.values(parsed).map(Number).filter(Boolean);
      const num = Number(parsed);
      if (!isNaN(num) && num > 0) return [num];
    } catch (e) { /* ignore */ }

    str = str.replace(/[[\]"]/g, '');
    return str.split(',').map(s => Number(s.trim())).filter(num => !isNaN(num) && num > 0);
  };

  // âœ… Ø¨Ù†Ø§Ø¡ Ø®Ø±Ø§Ø¦Ø· Ø´Ø§Ù…Ù„Ø© Ù…Ù† ALL Ø§Ù„Ù…ØµØ§Ø¯Ø± Ø§Ù„Ù…ØªØ§Ø­Ø©
  const allServicesMap = new Map();
  const allPackingMap = new Map();
  const allBuildUpMap = new Map();

  const addToMap = (map, list) => {
    if (Array.isArray(list)) {
      list.forEach(item => {
        if (item && item.id != null) map.set(Number(item.id), item);
      });
    }
  };

  // 1. Ù…Ù† Ø§Ù„Ù‚ÙˆØ§Ø¦Ù… Ø§Ù„Ù…ÙÙ…Ø±Ù‘ÙŽØ±Ø©
  addToMap(allServicesMap, servicesList);
  addToMap(allPackingMap, packingItems);
  addToMap(allBuildUpMap, buildUpItems);

  // 2. Ù…Ù† Ø§Ù„ÙƒØ§Ø¦Ù†Ø§Øª Ø§Ù„Ù…Ø¯Ù…Ø¬Ø© Ø¯Ø§Ø®Ù„ bol Ø£Ùˆ homes
  const collectEmbedded = (b) => {
    if (!b) return;
    addToMap(allServicesMap, b.services);
    addToMap(allPackingMap, b.packingItems);
    addToMap(allBuildUpMap, b.buildUpItems);
  };
  collectEmbedded(bol);
  if (Array.isArray(bol.homes)) bol.homes.forEach(collectEmbedded);

  // âœ… Ø¯ÙˆØ§Ù„ Ø§Ù„Ø¨Ø­Ø« Ø§Ù„Ù…Ø­Ø³Ù‘Ù†Ø©
  const getSelectedServices = (b) => {
    const listIds = parseIdArray(getVal(b, "service_ids") || getVal(b, "serviceIds"));
    const singleIds = parseIdArray(getVal(b, "service_id") || getVal(b, "serviceId"));
    const ids = Array.from(new Set([...listIds, ...singleIds]));

    if (ids.length > 0) {
      if (allServicesMap.size > 0) {
        const found = ids.map(id => allServicesMap.get(Number(id))).filter(Boolean);
        if (found.length > 0) return found;
      }

      if (servicesList.length > 0) {
        const found = ids.map(id => servicesList.find(s => Number(s.id) === id)).filter(Boolean);
        if (found.length > 0) return found;
      }
    }

    if (Array.isArray(b?.services) && b.services.length > 0) return b.services;
    return [];
  };

  const getSelectedPacking = (b) => {
    const listIds = parseIdArray(getVal(b, "packing_item_ids") || getVal(b, "packingItemIds"));
    const singleIds = parseIdArray(getVal(b, "packing_item_id") || getVal(b, "packingItemId"));
    const ids = Array.from(new Set([...listIds, ...singleIds]));

    if (ids.length > 0) {
      if (allPackingMap.size > 0) {
        const found = ids.map(id => allPackingMap.get(Number(id))).filter(Boolean);
        if (found.length > 0) return found;
      }

      if (packingItems.length > 0) {
        const found = ids.map(id => packingItems.find(p => Number(p.id) === id)).filter(Boolean);
        if (found.length > 0) return found;
      }
    }

    if (Array.isArray(b?.packingItems) && b.packingItems.length > 0) return b.packingItems;
    return [];
  };

  const getSelectedBuildUp = (b) => {
    const listIds = parseIdArray(getVal(b, "build_up_item_ids") || getVal(b, "buildUpItemIds"));
    const singleIds = parseIdArray(getVal(b, "build_up_item_id") || getVal(b, "buildUpItemId"));
    const ids = Array.from(new Set([...listIds, ...singleIds]));

    if (ids.length > 0) {
      if (allBuildUpMap.size > 0) {
        const found = ids.map(id => allBuildUpMap.get(Number(id))).filter(Boolean);
        if (found.length > 0) return found;
      }

      if (buildUpItems.length > 0) {
        const found = ids.map(id => buildUpItems.find(bu => Number(bu.id) === id)).filter(Boolean);
        if (found.length > 0) return found;
      }
    }

    if (Array.isArray(b?.buildUpItems) && b.buildUpItems.length > 0) return b.buildUpItems;
    return [];
  };

  const getCargoTypeDisplay = (b) => getVal(b, "cargo_type") || "GCR";

  const getServicesDisplay = (b) => {
    const svcs = getSelectedServices(b);
    if (svcs.length > 0) {
      return svcs.map(s => `${s.type || s.name || 'Service'}: ${s.classification || ""}`).join(", ");
    }
    return "None";
  };

  const getPackingDisplay = (b) => {
    const packs = getSelectedPacking(b);
    if (packs.length > 0) {
      return packs.map(p => `${p.type || p.name || 'Packing'} - ${p.classification || ""}`).join(", ");
    }
    return "None";
  };

  const getBuildUpDisplay = (b) => {
    const bus = getSelectedBuildUp(b);
    if (bus.length > 0) {
      return bus.map(bu => bu.name || 'Build-up').join(", ");
    }
    return "None";
  };

  const calculateSingleBolFees = (fWeight, fQty, fCargoType, fServices, fPacking, fBuildUp, fAwbCount) => {
    let sFee = 0, pFee = 0, bFee = 0;
    const priceKey = normalizeType(fCargoType || 'gcr');

    fServices.forEach(s => {
      const rate = (typeof s[priceKey] === 'string' && s[priceKey].toLowerCase() === 'free')
        ? 0
        : Number(s[priceKey] || 0);

      if (s.rate_type === 'per_kg') sFee += rate * fWeight;
      else if (s.rate_type === 'per_ton') sFee += rate * (fWeight / 1000);
      else if (s.rate_type === 'per_awb') sFee += rate * fAwbCount;
      else sFee += rate;
    });

    fPacking.forEach(p => pFee += (Number(p.price) || 0) * (Number(fQty) || 1));
    fBuildUp.forEach(b => bFee += Number(b.price) || 0);

    return { serviceFee: sFee, packingFee: pFee, buildUpFee: bFee, totalFee: sFee + pFee + bFee };
  };

  const getBolFees = (b) => {
    let fObj = b.fees;
    if (typeof fObj === 'string') {
      try { fObj = JSON.parse(fObj); } catch { fObj = {}; }
    }
    fObj = fObj || {};

    const serviceFee = fObj.service ?? fObj.serviceFee;
    const packingFee = fObj.packing ?? fObj.packingFee;
    const buildUpFee = fObj.buildUp ?? fObj.buildUpFee;

    if (serviceFee !== undefined || packingFee !== undefined || buildUpFee !== undefined) {
      // âœ… Ø¥Ø¬Ù…Ø§Ù„ÙŠ Ù‡Ø°Ø§ Ø§Ù„Ø³Ø·Ø± = Ù…Ø¬Ù…ÙˆØ¹ Ø§Ù„Ø®Ø¯Ù…Ø©+Ø§Ù„ØªØºÙ„ÙŠÙ+Ø§Ù„Ø¨Ù†Ø§Ø¡ Ø¯Ø§Ø¦Ù…Ø§Ù‹ (ØªØ¬Ø§Ù‡Ù„ Ø£ÙŠ total Ù…Ø­ÙÙˆØ¸ Ù‚Ø¯ ÙŠØ´Ù…Ù„ ØªØ®Ø²ÙŠÙ†/Ø¥ÙŠØµØ§Ù„)
      const sFee = toNumber(serviceFee);
      const pFee = toNumber(packingFee);
      const bFee = toNumber(buildUpFee);
      return { serviceFee: sFee, packingFee: pFee, buildUpFee: bFee, totalFee: sFee + pFee + bFee };
    }

    const grossWeight = toNumber(getVal(b, "gross_mass"));
    const volumetricWeight = toNumber(getVal(b, "vol_by_mc"));
    const weight = Math.max(grossWeight, volumetricWeight);
    const qty = toNumber(getVal(b, "number_of_packages"));
    const cargoType = getCargoTypeDisplay(b);
    const svcs = getSelectedServices(b);
    const packs = getSelectedPacking(b);
    const bus = getSelectedBuildUp(b);
    const awbCount = b === bol ? (bol.homes?.length > 0 ? bol.homes.length : 1) : 1;

    return calculateSingleBolFees(weight, qty, cargoType, svcs, packs, bus, awbCount);
  };

  const departureDate = getVal(bol, "date_of_arrival") || getVal(header, "date_of_arrival") || new Date();
  const storageEndDate = getVal(bol, "storage_end_date") || getVal(bol, "storageEndDate") || departureDate;

  // Info Grid
  const infoStartY = logoY + logoHeight + 55;
  let infoCurrentY = infoStartY;
  const col1X = margin;
  const col2X = pageW / 2 + 50;
  const lineHeight = 14;

  doc.setFontSize(10);
  doc.setFont("Amiri", "bold");
  doc.text("Date prepared:", col1X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(formatDate(new Date()), col1X + 80, infoCurrentY);

  doc.setFont("Amiri", "bold");
  doc.text("Page:", col2X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text("01", col2X + 60, infoCurrentY);
  infoCurrentY += lineHeight;

  doc.setFont("Amiri", "bold");
  doc.text("Owner or Operator:", col1X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "owner_operator") || getVal(header, "owner_operator") || "TARCO AVIATION"), col1X + 100, infoCurrentY);

  doc.setFont("Amiri", "bold");
  doc.text("Prepared by:", col2X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(bol.prepared_by || "___________"), col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight;

  doc.setFont("Amiri", "bold");
  doc.text("Registration:", col1X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "aircraft_registration") || getVal(bol, "registration") || getVal(header, "registration") || "___________"), col1X + 80, infoCurrentY);

  doc.setFont("Amiri", "bold");
  doc.text("Manifest No:", col2X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  const manifestNoValue = getVal(bol, "manifest_no") || getVal(header, "manifest_no") || getVal(bol, "manifestNo") || getVal(header, "manifestNo") || bol.id || header?.id || "___________";
  doc.text(fmt(manifestNoValue), col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight;

  doc.setFont("Amiri", "bold");
  doc.text("Point of Loading:", col1X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "port_of_loading") || getVal(bol, "place_of_departure_code") || getVal(header, "place_of_departure_code") || "___________"), col1X + 100, infoCurrentY);

  doc.setFont("Amiri", "bold");
  doc.text("Customs Ref:", col2X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "customs_office_code") || getVal(header, "customs_office_code") || "___________"), col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight;

  doc.setFont("Amiri", "bold");
  doc.text("Flight No:", col1X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "flight_no") || getVal(header, "flight_no") || getVal(bol, "voyage_flight_no") || getVal(header, "voyage_flight_no") || getVal(bol, "voyage_number") || getVal(header, "voyage_number") || "___________"), col1X + 80, infoCurrentY);

  doc.setFont("Amiri", "bold");
  doc.text("Point of Unloading:", col2X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "port_of_discharge") || getVal(bol, "place_of_destination_code") || getVal(header, "place_of_destination_code") || "___________"), col2X + 100, infoCurrentY);
  infoCurrentY += lineHeight;

  doc.setFont("Amiri", "bold");
  doc.text("ETD:", col2X, infoCurrentY);
  doc.setFont("Amiri", "normal");
  doc.text(fmt(getVal(bol, "etd")) || fmt(getVal(header, "etd")) || "-", col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight + 10;

  const masterBolStorageStartDate = getVal(bol, "storage_start_date");
  const masterBolStorageEndDate = getVal(bol, "storage_end_date");

  doc.setFont("Amiri", "bold");
  doc.text(`Storage Start Date:`, col1X, infoCurrentY);
  doc.text(formatDate(masterBolStorageStartDate) || "-", col1X + 100, infoCurrentY);
  doc.text(`Storage End Date:`, col2X, infoCurrentY);
  doc.text(formatDate(masterBolStorageEndDate) || "-", col2X + 100, infoCurrentY);

  let currentY = infoCurrentY + lineHeight + 15;

  const getStorageFee = (b) => {
    if (b === bol && savedInvoiceAmount !== undefined) return toNumber(savedInvoiceAmount);
    const invAmt = getVal(b, "invoice_amount");
    if (invAmt !== undefined && invAmt !== null) return toNumber(invAmt);
    const storageAmt = getVal(b, "storage_amount");
    if (storageAmt !== undefined && storageAmt !== null) return toNumber(storageAmt);
    const fee = b.storage_fee ?? b.storageFee;
    if (fee !== undefined && fee !== null) return toNumber(fee);
    return toNumber(b.fees?.storage ?? b.fees?.total ?? 0);
  };

  const getReceiptAmountFor = (b, isMaster = false) => {
    if (isMaster && savedReceiptAmount !== undefined && savedReceiptAmount !== null) {
      return toNumber(savedReceiptAmount);
    }
    return toNumber(b?.receipt_amount ?? b?.receiptAmount ?? 0);
  };

  const parseToLocal = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  };

  // âœ… Ù†ÙØ³ Ù…Ù†Ø·Ù‚ Ø§Ù„Ø³Ø§Ø¹Ø§Øª Ø§Ù„Ù…Ø¬Ø§Ù†ÙŠØ© Ø­Ø³Ø¨ Ù†ÙˆØ¹ Ø§Ù„Ø¨Ø¶Ø§Ø¹Ø© Ø§Ù„Ù…Ø³ØªØ®Ø¯Ù… ÙÙŠ Ø§Ù„Ø´Ø§Ø´Ø© (parseFreeHours + billableDays)
  const parseFreeHoursByCargoType = (cargoType) => {
    const freeHoursMap = { GCR: 72, PER: 48, DGR: 24 };
    return freeHoursMap[String(cargoType || "").toUpperCase()] || 0;
  };

  // âœ… Ø¥Ø¹Ø§Ø¯Ø© Ø­Ø³Ø§Ø¨ Ø§Ù„Ø£ÙŠØ§Ù… Ø¯Ø§Ø¦Ù…Ø§Ù‹ Ù…Ù† Ø§Ù„ØªÙˆØ§Ø±ÙŠØ® Ø§Ù„ÙØ¹Ù„ÙŠØ© Ø¨Ø¯Ù„ Ø§Ù„Ø§Ø¹ØªÙ…Ø§Ø¯ Ø¹Ù„Ù‰ Ù‚ÙŠÙ…Ø© Ù…Ø®Ø²Ù‘Ù†Ø© Ù‚Ø¯ ØªÙƒÙˆÙ† 0/Ù‚Ø¯ÙŠÙ…Ø©
  const calculateBolDays = (b, fbStart, fbEnd) => {
    const start = b.storage_start_date || b.storageStartDate || fbStart;
    const end = b.storage_end_date || b.storageEndDate || fbEnd || storageEndDate;
    if (!start || !end) return 0;

    const s = parseToLocal(start);
    const e = parseToLocal(end);
    let totalDays = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
    if (b.day || b.subtract_day) totalDays -= 1;
    if (totalDays < 0) totalDays = 0;

    const cargoType = b.cargo_type || b.cargoType || "GCR";
    const freeHours = parseFreeHoursByCargoType(cargoType);
    const totalHours = totalDays * 24;
    const billableHours = Math.max(0, totalHours - freeHours);
    return Math.ceil(billableHours / 24);
  };

  const getBillableDays = (b) => {
    const candidates = [b?.billable_days, b?.billableDays, b?.storage_days_total, b?.storageDaysTotal];
    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    // Fallback: recalculate only when no persisted billable day exists.
    const computed = calculateBolDays(b, getVal(bol, "storage_start_date"), getVal(bol, "storage_end_date"));
    if (computed > 0) return computed;

    const parsedInputDays = Number(days);
    if (b === bol && Number.isFinite(parsedInputDays) && parsedInputDays > 0) return parsedInputDays;

    return 0;
  };

  const totalPieces = Array.isArray(bol.homes) && bol.homes.length > 0
    ? bol.homes.reduce((sum, h) => sum + toNumber(getVal(h, "number_of_packages")), 0)
    : toNumber(getVal(bol, "number_of_packages"));

  const totalWeight = toNumber(getVal(bol, "gross_mass"));
  const masterFee = toNumber(
    getVal(bol, "storage_amount") ??
    getVal(bol, "storage_final_amount") ??
    getVal(bol, "storage_fee") ??
    bol?.storageFee ??
    0
  );
  const hasAggregateSavedInvoice = savedInvoiceAmount !== undefined && savedInvoiceAmount !== null;
  const housesFee = hasAggregateSavedInvoice
    ? 0
    : (bol.homes || []).reduce((sum, h) => sum + getStorageFee(h), 0);
  const finalStorageTotal = hasAggregateSavedInvoice
    ? toNumber(savedInvoiceAmount)
    : (masterFee + housesFee);
  const mFees = getBolFees(bol);
  const housesServicesTotal = (bol.homes || []).reduce((sum, h) => sum + getBolFees(h).totalFee, 0);
  const servicesBaseTotal = mFees.totalFee + housesServicesTotal;
  const servicesAfterDiscount = toNumber(savedReceiptAmount) > 0
    ? toNumber(savedReceiptAmount)
    : servicesBaseTotal;
  const finalTotalFee = finalStorageTotal + servicesAfterDiscount;

  const tableHeaders = ["Air Waybill", "Pieces", "Weight", "Nature of Goods", "Days", `Fee (${currencyLabel})`, "Official Use"];
  const tableRows = [];

  tableRows.push([{ content: "BULK CARGO", colSpan: 7, styles: { fontStyle: "bold" } }]);

  tableRows.push([
    fmt(getVal(bol, "bol_reference")),
    fmt(getVal(bol, "number_of_packages")),
    `${fmt(getVal(bol, "gross_mass"))} KG`,
    fmt(getVal(bol, "goods_description")),
    fmt(getBillableDays(bol)),
    formatMoney(masterFee * rateMultiplier),
    fmt(bol.official_use || "J EAP SPX.")
  ]);

  // âœ… Ø§Ù„Ø¨ÙˆÙ„ÙŠØµØ© Ø§Ù„Ù…Ø§Ø³ØªØ± ØªØ¹Ø±Ø¶ Ø±Ø³ÙˆÙ…Ù‡Ø§ Ø§Ù„Ø®Ø§ØµØ© ÙÙ‚Ø· (Ø¨Ø¯ÙˆÙ† Ø¬Ù…Ø¹ Ø±Ø³ÙˆÙ… Ø§Ù„Ù‡Ø§ÙˆØ³Ø§Øª Ø§Ù„ØªÙŠ ØªÙØ·Ø¨Ø¹ Ø¨Ø´ÙƒÙ„ Ù…Ù†ÙØµÙ„ Ø£Ø¯Ù†Ø§Ù‡)
  tableRows.push([
    {
      content: `    Cargo Types: ${getCargoTypeDisplay(bol)}  |  Services: ${getServicesDisplay(bol)}  |  Packing: ${getPackingDisplay(bol)}  |  Build-up: ${getBuildUpDisplay(bol)}\n    Service Fee: ${currencySymbol}${formatMoney(mFees.serviceFee * rateMultiplier)}  |  Packing Fee: ${currencySymbol}${formatMoney(mFees.packingFee * rateMultiplier)}  |  Build-up Fee: ${currencySymbol}${formatMoney(mFees.buildUpFee * rateMultiplier)}  |  Total: ${currencySymbol}${formatMoney(mFees.totalFee * rateMultiplier)}`,
      colSpan: 7,
      styles: { fontSize: 8, textColor: [100, 100, 100], fontStyle: "italic", cellPadding: 4 }
    }
  ]);

  if (Array.isArray(bol.homes)) {
    bol.homes.forEach(h => {
      const hFees = getBolFees(h);
      tableRows.push([
        `    * ${fmt(getVal(h, "bol_reference"))}`,
        fmt(getVal(h, "number_of_packages")),
        `${fmt(getVal(h, "gross_mass"))} KG`,
        fmt(getVal(h, "goods_description")),
        fmt(getBillableDays(h)),
        formatMoney(getStorageFee(h) * rateMultiplier),
        fmt(h.official_use || "J EAP SPX.")
      ]);
      tableRows.push([
        {
          content: `        Cargo Types: ${getCargoTypeDisplay(h)}  |  Services: ${getServicesDisplay(h)}  |  Packing: ${getPackingDisplay(h)}  |  Build-up: ${getBuildUpDisplay(h)}\n        Service Fee: ${currencySymbol}${formatMoney(hFees.serviceFee * rateMultiplier)}  |  Packing Fee: ${currencySymbol}${formatMoney(hFees.packingFee * rateMultiplier)}  |  Build-up Fee: ${currencySymbol}${formatMoney(hFees.buildUpFee * rateMultiplier)}  |  Total: ${currencySymbol}${formatMoney(hFees.totalFee * rateMultiplier)}`,
          colSpan: 7,
          styles: { fontSize: 8, textColor: [100, 100, 100], fontStyle: "italic", cellPadding: 4 }
        }
      ]);
    });
  }

  tableRows.push([{ content: `Services Total: ${currencySymbol}${formatMoney(servicesBaseTotal * rateMultiplier)}`, colSpan: 7, styles: { fontStyle: "bold" } }]);
  tableRows.push([{ content: `Services After Discount: ${currencySymbol}${formatMoney(servicesAfterDiscount * rateMultiplier)}`, colSpan: 7, styles: { fontStyle: "bold" } }]);
  tableRows.push([{ content: `Storage Fees Total: ${currencySymbol}${formatMoney(finalStorageTotal * rateMultiplier)}`, colSpan: 7, styles: { fontStyle: "bold" } }]);

  autoTable(doc, {
    startY: currentY,
    head: [tableHeaders],
    body: tableRows,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 5, textColor: [0, 0, 0], font: "Amiri" },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
    foot: [[
      "TOTAL",
      `${totalPieces} PCS`,
      `${totalWeight} KG`,
      { content: "", colSpan: 2 },
      formatMoney(finalTotalFee * rateMultiplier)
    ]],
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
    didDrawCell: (data) => {
      if (data.section === 'head') {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(1);
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
      }
    }
  });

  const finalY = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(11);
  const invoiceNoValue = invoice_no || d_no || getVal(bol, "invoice_no") || getVal(bol, "d_no") || getVal(bol, "dNo") || bol.invoiceNo || bol.dNo || "";
  doc.text(`Invoice No: ${fmt(invoiceNoValue)}`, margin, finalY);
  doc.text(`Manifest No: ${fmt(manifestNoValue)}`, margin + 200, finalY);

  doc.save(`Invoice_${fmt(bol.bol_reference || bol.bolReference)}.pdf`);
}
